import type { ApolloClient, ErrorLike } from "@apollo/client";
import { data } from "react-router";
import { filterOutWithheldEntityErrors } from "./filter-out-withheld-entity-errors";
import { extractErrorCodes } from "./mapApolloErrors";

function castApolloError(error: ErrorLike) {
  const errorCodes = new Set(extractErrorCodes(error));

  if (
    errorCodes.has("Unauthorised") ||
    errorCodes.has("UNAUTHORISED") ||
    errorCodes.has("NotAuthorised") ||
    errorCodes.has("NOT_AUTHORISED")
  ) {
    return data("unauthorised", 403);
  }

  if (errorCodes.has("NotFound") || errorCodes.has("NOT_FOUND")) {
    return data("not_found", 404);
  }

  return data(error, 500);
}

/**
 * Route-loader invariant over an Apollo query result: the data, or a thrown
 * route response classified from the error (UNAUTHORISED / NOT_AUTHORISED →
 * 403, NOT_FOUND → 404, anything else → 500).
 *
 * Entity-withheld denials are stripped first, judged against the data they
 * arrived beside (`filterOutWithheldEntityErrors`): a withheld entity is a
 * key-only stub inside an otherwise good response and never fails the route.
 * Only what survives is classified; when nothing survives the partial data IS
 * the response, and null content on it means WITHHELD.
 *
 * Loaders reach this through `loaderQuery`, which runs the query with the
 * `errorPolicy: "all"` this invariant needs — under the default policy the
 * client rejects before the result exists.
 */
export function apolloDataInvariant<T>(result: ApolloClient.QueryResult<T>) {
  const error = filterOutWithheldEntityErrors(result.error, result.data);
  if (error) {
    throw castApolloError(error);
  }

  return result.data;
}
